import React, { useMemo } from 'react';
import { Clock } from 'lucide-react';

const StateTransitionSwimlane = ({ data }) => {
  const swimlaneData = useMemo(() => {
    return data.slice(0, 15).map(item => {
      const states = item.raw_data?.states || [];
      const stateCount = item.raw_data?.state_count || 1;
      
      // Create timeline segments
      const segments = [];
      if (states.length > 0) {
        states.forEach((state, idx) => {
          segments.push({
            state: state.metric_value || item.state,
            start: state.metric_start,
            end: state.metric_end,
            health: item.health,
            healthScore: item.healthScore
          });
        });
      } else {
        // Fallback if no states array
        segments.push({
          state: item.state,
          start: item.date,
          end: 'Current',
          health: item.health,
          healthScore: item.healthScore
        });
      }

      return {
        changeNumber: item.changeNumber,
        description: item.description,
        segments,
        currentState: item.state,
        stateCount,
        health: item.health,
        healthScore: item.healthScore
      };
    });
  }, [data]);

  const getHealthColor = (score) => {
    if (score >= 80) return '#10b981';
    if (score >= 60) return '#eab308';
    if (score >= 30) return '#f97316';
    return '#ef4444';
  };

  const getStateColor = (state) => {
    if (state.includes('Complete') || state.includes('Closed')) return '#b2ff59';
    if (state.includes('Prod')) return '#00e5ff';
    if (state.includes('Progress') || state.includes('Verification')) return '#eab308';
    return '#b24bf3';
  };

  return (
    <div className="bg-dark-card rounded-lg border border-dark-border p-6">
      <div className="flex items-center gap-3 mb-6">
        <Clock size={24} className="text-cyan-accent" />
        <h3 className="text-xl font-bold text-cyan-accent">State Transition Swimlane</h3>
        <span className="text-sm text-gray-500">(Top 15 changes)</span>
      </div>

      <div className="space-y-3">
        {swimlaneData.map((item, idx) => (
          <div key={idx} className="bg-dark-border/20 rounded-lg p-3 border border-dark-border/60">
            {/* Change Header */}
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-3">
                <span className="text-sm font-bold text-white">{item.changeNumber}</span>
                <span className="text-xs text-gray-500 truncate max-w-xs">
                  {item.description.substring(0, 40)}...
                </span>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-xs px-2 py-1 rounded bg-dark-border/40 text-gray-400">
                  {item.stateCount} state{item.stateCount > 1 ? 's' : ''}
                </span>
                <span 
                  className="text-xs px-2 py-1 rounded font-semibold"
                  style={{ 
                    backgroundColor: `${getHealthColor(item.healthScore)}20`,
                    color: getHealthColor(item.healthScore),
                    border: `1px solid ${getHealthColor(item.healthScore)}40`
                  }}
                >
                  {item.healthScore}
                </span>
              </div>
            </div>

            {/* Timeline */}
            <div className="relative h-10 bg-dark-border/40 rounded-lg overflow-hidden border border-dark-border/60">
              <div className="absolute inset-0 flex">
                {item.segments.map((segment, segIdx) => {
                  const width = 100 / item.segments.length;
                  return (
                    <div
                      key={segIdx}
                      className="relative transition-all hover:opacity-80 cursor-pointer group"
                      style={{
                        width: `${width}%`,
                        backgroundColor: getStateColor(segment.state),
                        boxShadow: `inset 0 0 20px ${getHealthColor(segment.healthScore)}40`,
                        borderRight: segIdx < item.segments.length - 1 ? '2px solid #1a1a1a' : 'none'
                      }}
                      title={`${segment.state} - Health: ${segment.healthScore}`}
                    >
                      {/* State Label */}
                      <div className="absolute inset-0 flex items-center justify-center">
                        <span className="text-xs font-semibold text-white opacity-0 group-hover:opacity-100 transition-opacity">
                          {segment.state.length > 10 ? segment.state.substring(0, 8) + '...' : segment.state}
                        </span>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Current State */}
            <div className="mt-2 flex items-center justify-between text-xs">
              <span className="text-gray-500">Current:</span>
              <span 
                className="font-semibold px-2 py-0.5 rounded"
                style={{ color: getStateColor(item.currentState) }}
              >
                {item.currentState}
              </span>
            </div>
          </div>
        ))}
      </div>

      {/* Legend */}
      <div className="mt-6 pt-4 border-t border-dark-border/50 flex items-center justify-center gap-6 text-xs">
        <div className="flex items-center gap-2">
          <div className="w-3 h-3 rounded" style={{ backgroundColor: '#b2ff59' }}></div>
          <span className="text-gray-400">Complete</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-3 h-3 rounded" style={{ backgroundColor: '#00e5ff' }}></div>
          <span className="text-gray-400">Production</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-3 h-3 rounded" style={{ backgroundColor: '#eab308' }}></div>
          <span className="text-gray-400">In Progress</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-3 h-3 rounded" style={{ backgroundColor: '#b24bf3' }}></div>
          <span className="text-gray-400">Other</span>
        </div>
      </div>
    </div>
  );
};

export default StateTransitionSwimlane;
